import { useEffect, useState } from "react";
import { adminApi } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Settings, Shield, Save, CheckCircle } from "lucide-react";

const roleLabels = {
  admin: "System Admin",
  executive: "Executive (COO/AM)",
  crew_scheduling: "Crew Scheduling Mgr",
  engineering_manager: "Engineering Manager",
  safety_manager: "Safety Manager",
  pilot: "Pilot (Self-View)",
  ame: "AME (Self-View)",
};

export default function AdminSettings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");

  useEffect(() => {
    Promise.all([adminApi.getUsers(), adminApi.getSettings()])
      .then(([u, s]) => { setUsers(u.data); setSettings(s.data); })
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = async () => {
    await adminApi.updateSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <div className="flex-1 p-6 flex items-center justify-center"><div className="text-primary font-mono text-sm animate-pulse">LOADING ADMIN...</div></div>;

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="admin-settings">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 10</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Admin & Policy Settings</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border">
        {[["settings", "Fatigue Thresholds", Settings], ["users", "User Management", Users], ["fdtl", "FDTL Rules", Shield]].map(([key, lbl, Icon]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${activeTab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`admin-tab-${key}`}>
            <Icon size={14} />{lbl}
          </button>
        ))}
      </div>

      {activeTab === "settings" && settings && (
        <div className="max-w-2xl space-y-6">
          <div className="bg-card border border-border rounded-sm p-5 space-y-5">
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Fatigue Score Thresholds</div>

            {[
              ["fit_threshold", "Fit / Caution Boundary", "Scores at or below this value are classified as FIT", "text-green-400"],
              ["caution_threshold", "Caution / High Risk Boundary", "Scores above this enter HIGH RISK classification", "text-amber-400"],
              ["high_risk_threshold", "High Risk / Do Not Assign Boundary", "Scores above this trigger DO NOT ASSIGN status", "text-red-400"],
            ].map(([key, lbl, desc, col]) => (
              <div key={key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-foreground">{lbl}</label>
                  <span className={`font-mono text-lg font-medium ${col}`}>{settings[key]}</span>
                </div>
                <input type="range" min={10} max={100} value={settings[key]}
                  onChange={e => setSettings({...settings, [key]: parseInt(e.target.value)})}
                  className="w-full accent-primary" data-testid={`threshold-${key}`} />
                <p className="text-xs text-muted-foreground mt-1">{desc}</p>
              </div>
            ))}

            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between p-3 bg-background border border-border rounded-sm mb-3">
                <div>
                  <div className="text-sm font-medium text-foreground">Manager approval for overrides</div>
                  <div className="text-xs text-muted-foreground mt-0.5">All alert overrides require manager confirmation</div>
                </div>
                <button onClick={() => setSettings({...settings, override_requires_manager: !settings.override_requires_manager})}
                  className={`w-11 h-6 rounded-full transition-colors ${settings.override_requires_manager ? "bg-primary" : "bg-secondary"}`}
                  data-testid="toggle-override-approval">
                  <span className={`block w-4 h-4 bg-white rounded-full transition-transform mx-1 ${settings.override_requires_manager ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-background border border-border rounded-sm">
                <div>
                  <div className="text-sm font-medium text-foreground">AI Analysis Engine</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Enable Claude AI for fatigue predictions</div>
                </div>
                <button onClick={() => setSettings({...settings, ai_enabled: !settings.ai_enabled})}
                  className={`w-11 h-6 rounded-full transition-colors ${settings.ai_enabled ? "bg-primary" : "bg-secondary"}`}
                  data-testid="toggle-ai">
                  <span className={`block w-4 h-4 bg-white rounded-full transition-transform mx-1 ${settings.ai_enabled ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            </div>

            <div>
              <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-2">FDTL Rules Version</div>
              <div className="bg-background border border-border rounded-sm px-3 py-2 font-mono text-sm text-foreground">{settings.fdtl_rules_version}</div>
            </div>

            <button onClick={saveSettings}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-semibold transition-colors ${saved ? "bg-green-500 text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
              data-testid="save-settings-button">
              {saved ? <><CheckCircle size={14} /> Saved!</> : <><Save size={14} /> Save Settings</>}
            </button>
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-border">
                {["Name", "Email", "Role", "Station"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className={`border-b border-border/50 row-hover ${u.id === user?.id ? "bg-primary/5" : ""}`} data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-sm bg-secondary flex items-center justify-center text-xs font-bold text-primary">{u.name[0]}</div>
                      <span className="text-xs text-foreground">{u.name}</span>
                      {u.id === user?.id && <span className="text-xs text-primary font-mono">(you)</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-sm">{roleLabels[u.role] || u.role}</span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-foreground">{u.station}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "fdtl" && (
        <div className="max-w-2xl space-y-3">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-3 flex items-start gap-2 mb-4">
            <Shield size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-300">FDTL rules are locked to DGCA regulations. Contact your system administrator to request rule amendments through official channels.</div>
          </div>
          {[
            ["Maximum FDP (non-augmented)", "13 hours", "DGCA CAR Section 7 Series M Part V"],
            ["Maximum FDP (augmented, 2 pilots)", "16 hours", "DGCA CAR Section 7 Series M Part V"],
            ["Minimum Rest Period", "12 hours", "DGCA CAR Section 7 Series M Part V"],
            ["Maximum Weekly FDP", "60 hours", "DGCA CAR Section 7 Series M Part V"],
            ["Maximum Night Duties (rolling 7d)", "4 nights", "Company Policy Extension to DGCA"],
            ["Cumulative FDP (rolling 28d)", "210 hours", "DGCA CAR Section 7 Series M Part V"],
            ["Early Start Definition", "Reporting before 07:00 local", "Company Policy"],
            ["Late Finish Definition", "FDP ending after 23:00 local", "Company Policy"],
          ].map(([rule, limit, source]) => (
            <div key={rule} className="bg-card border border-border rounded-sm p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="text-xs font-medium text-foreground">{rule}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{source}</div>
              </div>
              <div className="font-mono text-sm font-medium text-primary ml-4">{limit}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
